import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { upsertInvoiceAddress } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Address, AddressFormData } from "~/types/addressTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = AddressFormData & {
	buyerEAddress: string;
	recipientLegalName: string;
}

type Props = {
	address: Address | null;
	buyerEAddress: string | null;
	invoiceId: string;
	onCancel: () => void;
	onSuccess: () => void;
	recipientLegalName: string | null;
};

const UpdateInvoiceAddressForm = ({
	address,
	buyerEAddress,
	invoiceId,
	onCancel,
	onSuccess,
	recipientLegalName,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().required().default(""),
			addressLine2: yup.string().default(""),
			addressLine3: yup.string().default(""),
			buyerEAddress: yup.string().email().required().default(""),
			city: yup.string().required().default(""),
			countryCode: yup.string().required().default(""),
			recipientLegalName: yup.string().required().default(""),
			postCode: yup.string().required().default(""),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			addressLine1: address?.addressLine1 || "",
			addressLine2: address?.addressLine2 || "",
			addressLine3: address?.addressLine3 || "",
			buyerEAddress: buyerEAddress || "",
			city: address?.city || "",
			countryCode: address?.countryCode || DEFAULT_COUNTRY_CODE,
			recipientLegalName: recipientLegalName || "",
			postCode: address?.postCode || "",
		};
	}, [address?.addressLine1, address?.addressLine2, address?.addressLine3, address?.city, address?.countryCode, address?.postCode, buyerEAddress, recipientLegalName]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);

			const { addressLine1, addressLine2, addressLine3, buyerEAddress, city, postCode, countryCode, recipientLegalName } = data;

			const payload = {
				buyerEAddress,
				recipientLegalName,
				address: { addressLine1, addressLine2, addressLine3, city, postCode, countryCode },
			};

			await upsertInvoiceAddress(invoiceId, payload);

			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [invoiceId, onSuccess]);


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnungsadresse</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Rechnungsempfänger">
						<Textarea
							rows={3}
							control={control}
							name="recipientLegalName"
							placeholder="Firmenname / Voller Name Privatperson / Rechnungskopf"
						/>
					</FormSection>

					<AddressFormSection<FormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
						initialShowLine3={!!address?.addressLine3}
					/>
					<FormSection title="E-Mail-Adresse für E-Rechnung">
						<Input control={control}
							   name="buyerEAddress"
							   placeholder="rechnungseingang@kunde.de" />
						<div className="h-2"/>
						<Hint theme="info" size="sm">
							Diese E-Mail-Adresse wird ausschließlich für den Eintrag in der E-Rechnung verwendet.
						</Hint>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.save")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateInvoiceAddressForm;
